import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { fetchSuperAdminLoginAnalytics } from "../api/superadmin";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import "./ReportsSectionCompact.css";

function formatBrowserDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function defaultFilters() {
  const today = new Date();
  const end = formatBrowserDate(today);
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 29);
  return {
    start_date: formatBrowserDate(startDate),
    end_date: end,
    platform: "",
    event_type: "",
    session_context: "",
    outcome: "",
    role: "",
    user_id: "",
    ip_address: "",
    device_type: "",
    city: "",
    province: "",
    country: "",
    postal_code: "",
    search: "",
  };
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value ?? 0}</div>
    </div>
  );
}

function BreakdownTable({ title, rows = [] }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">
        {title}
      </div>
      <table className="min-w-full text-sm">
        <tbody className="divide-y divide-slate-100">
          {rows.length ? rows.slice(0, 10).map((row) => (
            <tr key={`${title}-${row.value}`}>
              <td className="px-4 py-2 text-slate-700">{row.label || row.value || "Unknown"}</td>
              <td className="px-4 py-2 text-right font-semibold text-slate-900">{row.count}</td>
            </tr>
          )) : (
            <tr><td className="px-4 py-4 text-center text-slate-500">No data</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function SuperAdminLoginAnalytics() {
  const { is_super_admin } = useAuth();
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadData = async (nextFilters = filters) => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchSuperAdminLoginAnalytics(nextFilters);
      setData(payload);
    } catch (err) {
      setError(err?.message || "Unable to load login analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (is_super_admin) {
      loadData(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_super_admin]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const clearFilters = () => {
    const next = defaultFilters();
    setFilters(next);
    loadData(next);
  };

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const breakdowns = data?.breakdowns || {};

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="overflow-hidden rounded-3xl bg-white shadow-2xl">
          <ReportPageHeader title="Login Analytics" closeTo="/superadmin/reports" />
          <div className={`space-y-6 px-6 py-6 ${MODAL_TEXT}`}>
            <ReportHelpCard
              title="Help Guide: Login Analytics"
              subtitle="Track login volume, access issues, MFA activity, device use, and platform movement."
              sections={[
                {
                  id: "filters",
                  title: "Filters",
                  description:
                    "Use the report filters to analyze login activity by date, platform, role, outcome, user ID, IP address, device, and salon location.",
                  anchorId: "sa-login-analytics-filters",
                  bullets: [
                    "Trend dates are grouped by the login event salon or business timezone.",
                    "Shared location filters above the report can be used with these local filters.",
                    "The operational event list lives under Super Admin > Login Activity.",
                    "Location fields are derived from the login IP address when available, with salon location kept for CSV context.",
                  ],
                },
              ]}
            />

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}

            <div id="sa-login-analytics-filters" className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <label className={LABEL_TEXT}>Start date</label>
                  <input className={`${FIELD_CLASS} w-full`} type="date" value={filters.start_date} onChange={(event) => updateFilter("start_date", event.target.value)} />
                </div>
                <div>
                  <label className={LABEL_TEXT}>End date</label>
                  <input className={`${FIELD_CLASS} w-full`} type="date" value={filters.end_date} onChange={(event) => updateFilter("end_date", event.target.value)} />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Platform</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.platform} onChange={(event) => updateFilter("platform", event.target.value)}>
                    <option value="">All platforms</option>
                    <option value="Ellyanna Booking">Ellyanna Booking</option>
                    <option value="MyBoothShare">MyBoothShare</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Event</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.event_type} onChange={(event) => updateFilter("event_type", event.target.value)}>
                    <option value="">All events</option>
                    <option value="login_success">Login success</option>
                    <option value="login_failed">Login failed</option>
                    <option value="mfa_required">MFA required</option>
                    <option value="mfa_completed">MFA completed</option>
                    <option value="cross_launch">Cross-launch</option>
                    <option value="logout">Logout</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Outcome</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.outcome} onChange={(event) => updateFilter("outcome", event.target.value)}>
                    <option value="">All outcomes</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                    <option value="blocked">Blocked</option>
                    <option value="mfa_required">MFA required</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Role</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.role} onChange={(event) => updateFilter("role", event.target.value)} placeholder="Role" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>User ID</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.user_id} onChange={(event) => updateFilter("user_id", event.target.value)} placeholder="User ID" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>IP Address</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.ip_address} onChange={(event) => updateFilter("ip_address", event.target.value)} placeholder="IP address" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Device</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.device_type} onChange={(event) => updateFilter("device_type", event.target.value)} placeholder="desktop, mobile, tablet" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>City</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.city} onChange={(event) => updateFilter("city", event.target.value)} placeholder="IP city" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Province / State</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.province} onChange={(event) => updateFilter("province", event.target.value)} placeholder="IP province or state" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Country</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.country} onChange={(event) => updateFilter("country", event.target.value)} placeholder="Country" />
                </div>
                <div className="xl:col-span-2">
                  <label className={LABEL_TEXT}>Search</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.search} onChange={(event) => updateFilter("search", event.target.value)} placeholder="Name, email, IDs, professional ID, IP, location" />
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-3">
                <button type="button" className={BTN_OUTLINE} onClick={clearFilters}>Clear</button>
                <button type="button" className={BTN_PRIMARY} onClick={() => loadData(filters)} disabled={loading}>
                  {loading ? "Running..." : "Run Report"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
              <SummaryCard label="Total events" value={summary.total_events} />
              <SummaryCard label="Successful logins" value={summary.successful_logins} />
              <SummaryCard label="Failed logins" value={summary.failed_logins} />
              <SummaryCard label="Blocked" value={summary.blocked_logins} />
              <SummaryCard label="Unique users" value={summary.unique_users} />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <BreakdownTable title="By platform" rows={breakdowns.platform} />
              <BreakdownTable title="By event" rows={breakdowns.event_type} />
              <BreakdownTable title="By outcome" rows={breakdowns.outcome} />
              <BreakdownTable title="By role" rows={breakdowns.role} />
              <BreakdownTable title="By device" rows={breakdowns.device_type} />
              <BreakdownTable title="By browser" rows={breakdowns.browser} />
              <BreakdownTable title="By IP address" rows={breakdowns.ip_address} />
              <BreakdownTable title="By city" rows={breakdowns.city} />
              <BreakdownTable title="By timezone" rows={breakdowns.timezone} />
            </div>

            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Local date</th>
                    <th className="px-4 py-3 text-right font-semibold">Total</th>
                    <th className="px-4 py-3 text-right font-semibold">Success</th>
                    <th className="px-4 py-3 text-right font-semibold">Failed</th>
                    <th className="px-4 py-3 text-right font-semibold">Blocked</th>
                    <th className="px-4 py-3 text-right font-semibold">Ellyanna</th>
                    <th className="px-4 py-3 text-right font-semibold">MyBoothShare</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data?.time_series || []).length ? data.time_series.map((row) => (
                    <tr key={row.date}>
                      <td className="px-4 py-3 text-slate-700">{row.date}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">{row.total}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{row.success}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{row.failed}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{row.blocked}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{row.ellyanna}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{row.myboothshare}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">No trend data found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
